import { type FC, useMemo } from 'react';
import { IconButton, VStack } from '@chakra-ui/react';
import {
  UiStack,
  UiText,
  UiIconCurrencyCircleDollar,
  uiStyles,
  UiHStack,
  UiGrid,
  UiIconPlusCircle,
  UiIconMinusCircle,
  UiIconXCircle,
  UiSpinner,
  UiVStack
} from '@/lib/ui';
import { type CartItem, type CartItemDeleteRequest, type CartItemUpdateRequest } from '@/api/registration';
import { registration } from '@/api';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type ApiResponse } from '@/api/tenantClient';
import { useTenantApi } from '@/account/hook/useTenantApi';
import { useEventSettingsQuery } from '@/registration/hook/useEventSettingsQuery';
import { useRegisterRoute } from '@/registration/hook/useRegisterRoute';
import BaseMessageBarError from '@/base/MessageBar/Error';

export interface ItemProps {
  itemId: number
  name: string
  priceFormat: string
  quantity: number
  cartId: number
  registrationId: number
  errors?: string[]
}

const Item: FC<ItemProps> = ({
  itemId,
  name,
  priceFormat,
  quantity,
  cartId,
  registrationId,
  errors,
}) => {
  const queryClient = useQueryClient();

  const { createTenantApiRequest } = useTenantApi();
  const { eventId } = useRegisterRoute();
  const { data: eventSetting } = useEventSettingsQuery(eventId);

  const { mutate: updateMutate, isLoading: updateMutateIsLoading } = useMutation<ApiResponse<CartItem>, Error, CartItemUpdateRequest>({
    mutationFn: async (data: CartItemUpdateRequest) => {
      return await registration.updateCartItem(createTenantApiRequest)(data);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [registration.cartQueryKey, { registrationId }] }),
        queryClient.invalidateQueries({ queryKey: [registration.registrationQueryKey, { eventId }] }),
      ]);
    },
    onError: () => {
      // NOTE: handle error
    }
  });
  // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
  const { mutate: deleteMutate, isLoading: deleteMutateIsLoading } = useMutation<void, Error, CartItemDeleteRequest>({
    mutationFn: async (request: CartItemDeleteRequest) => {
      await registration.deleteCartItem(createTenantApiRequest)(request);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [registration.cartQueryKey, { registrationId }] }),
        queryClient.invalidateQueries({ queryKey: [registration.registrationQueryKey, { eventId }] }),
      ]);
    },
    onError: () => {
      // NOTE: handle error
    }
  });

  const updateQuantity = (q: number) => {
    updateMutate({ id: itemId, quantity: q, cartId });
  };

  const deleteCartItem = () => {
    if (!deleteMutateIsLoading) { deleteMutate({ id: itemId }); }
  };

  return (
    <UiVStack alignItems={'stretch'}>
      <UiStack
        p={8}
        bgColor={'#fff'}
        borderRadius={uiStyles.borderRadius}
        shadow={uiStyles.cardShadow}
      >
        <UiGrid
          templateColumns={{ base: '1fr', lg: '1fr 1fr' }}
          gap={8}
          alignItems={'flex-end'}
        >
          <UiStack spacing={4}>
            <UiText variant={'body1'} fontWeight={600}>{name}</UiText>
            <UiHStack justifyContent={'flex-start'}>
              <UiIconCurrencyCircleDollar size={'2xl'} color={'text.primary'} />
              <UiText>
              Price
              </UiText>
              <UiText>
                {priceFormat}
              </UiText>
            </UiHStack>
            <UiHStack
              {...uiStyles.hover}
              // pt={2}
              onClick={!deleteMutateIsLoading ? deleteCartItem : undefined}
            >
              {deleteMutateIsLoading ? <UiSpinner size="sm" /> : <UiIconXCircle color={'red.500'} size={'2xl'} />}
              <UiText color={'red.500'}>Remove</UiText>
            </UiHStack>
          </UiStack>
          <UiStack flexGrow={1}>
            <UiHStack
              spacing={4}
              alignItems={'center'}
              // justifyContent={{base: 'flex-end', lg: 'flex-end'}}
              justifyContent={'flex-end'}
            >
              {
                eventSetting?.groupRegistration.isEnabled && (
                  <UiStack
                    {...uiStyles.hover}
                  >
                    <IconButton
                      aria-label={'Remove'}
                      icon={<UiIconMinusCircle color={'primary.500'} size={'4xl'} weight={'duotone'} />}
                      onClick={() => { return updateQuantity(quantity - 1); } } />
                  </UiStack>
                )
              }
              {
                updateMutateIsLoading
                  ? <UiSpinner size="md" />
                  : <UiText variant={'title'}>{quantity}</UiText>
              }
              {
                eventSetting?.groupRegistration.isEnabled && (
                  <UiStack
                    {...uiStyles.hover}
                  >
                    <IconButton
                      aria-label={'Add item to cart'}
                      icon={<UiIconPlusCircle color={'primary.500'} size={'4xl'} weight={'duotone'} />}
                      onClick={() => { return updateQuantity(quantity + 1); }} />
                  </UiStack>
                )
              }
            </UiHStack>
          </UiStack>
        </UiGrid>
      </UiStack>
      {
        errors && errors.length > 0 && (
          <UiStack flexGrow={1} paddingBottom={4}>
            {errors.map((error) => {
              return (
                <BaseMessageBarError key={error} mt={3}>{error}</BaseMessageBarError>
              );
            })}
          </UiStack>
        )}

    </UiVStack>
  );
};

export default Item;
